import { ThemeToggle } from "@/components/Theme";
import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common";
import { useAuth, homeFor } from "@/context/AuthContext";

const LINKS = [["Home", "/"], ["Find a Doctor", "/doctors"], ["Services", "/#services"], ["Suvidha Centre", "/#suvidha"], ["Nirogyam AI", "/#nirogyam-ai"], ["How It Works", "/#how"], ["About Us", "/#about"]];

export function PublicHeader() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E2E8ED]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
        <Logo className="h-9 sm:h-10" />
        <nav className="hidden xl:flex items-center gap-6 text-sm">
          {LINKS.map(([l, to]) => (
            <a key={l} href={to} data-testid={`nav-${l.toLowerCase().replace(/\s/g, "-")}`} className="whitespace-nowrap text-[#17232E]/80 hover:text-[#2412B5] transition-colors">{l}</a>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <ThemeToggle />
          {user ? (
            <Button data-testid="nav-dashboard-btn" onClick={() => nav(homeFor(user.role))} className="rounded-full">My dashboard</Button>
          ) : (
            <>
              <Button variant="outline" data-testid="nav-partner-portal-btn" onClick={() => nav("/partners")} className="rounded-full border-[#2412B5]/30 text-[#2412B5]">Partner Portal</Button>
              <Button variant="ghost" data-testid="nav-sign-in-btn" onClick={() => nav("/auth")} className="rounded-full">Sign In</Button>
              <Button data-testid="nav-book-btn" onClick={() => nav("/doctors")} className="rounded-full">Book a Consultation</Button>
            </>
          )}
        </div>
        <div className="xl:hidden flex items-center gap-1"><ThemeToggle />
        <button className="p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button></div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-[#E2E8ED] bg-white px-5 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map(([l, to]) => <a key={l} href={to} onClick={() => setOpen(false)} className="block py-2 text-sm">{l}</a>)}
          <div className="flex gap-2 pt-3">
            {user ? <Button className="flex-1 rounded-full" onClick={() => nav(homeFor(user.role))}>My dashboard</Button> : (
              <>
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => nav("/partners")} data-testid="mobile-partner-btn">Partners</Button>
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => nav("/auth")}>Sign In</Button>
                <Button className="flex-1 rounded-full" onClick={() => nav("/doctors")}>Book</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-[#E2E8ED] bg-[#F7F9FA]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <img src="/logo.png" alt="Nirogyam" className="h-12" />
          <p className="mt-4 text-sm muted max-w-sm">Nirogyam Teleclinic is operated by Nirogyam Diagnostics and Healthcare Pvt Ltd, Bhubaneswar, Odisha, India.</p>
          <p className="mt-3 text-xs text-red-700">Not for emergencies. In a medical emergency call 112 or visit the nearest hospital.</p>
        </div>
        <div className="space-y-3 text-sm">
          <p className="eyebrow">Contact</p>
          <a href="mailto:info@nirogyam.net" className="flex items-center gap-2 hover:text-[#2412B5]" data-testid="footer-email"><Mail className="h-4 w-4" /> info@nirogyam.net</a>
          <a href="tel:+917815007513" className="flex items-center gap-2 hover:text-[#2412B5]" data-testid="footer-phone"><Phone className="h-4 w-4" /> +91 78150 07513 (Call / WhatsApp)</a>
          <p className="flex items-center gap-2 muted"><MapPin className="h-4 w-4" /> Bhubaneswar, Odisha</p>
        </div>
        <div className="space-y-3 text-sm">
          <p className="eyebrow">Company</p>
          <Link to="/privacy" className="block hover:text-[#2412B5]" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/terms" className="block hover:text-[#2412B5]" data-testid="footer-terms">Terms of Use</Link>
          <Link to="/terms/doctors" className="block hover:text-[#2412B5]">Doctor Terms & Privacy</Link>
          <Link to="/terms/partners" className="block hover:text-[#2412B5]">Partner Terms & Privacy</Link>
          <Link to="/cookies" className="block hover:text-[#2412B5]" data-testid="footer-cookies">Cookie Statement</Link>
          <Link to="/partners" className="block hover:text-[#2412B5]" data-testid="footer-partner-portal">Partner Portal</Link>
          <Link to="/help" className="block hover:text-[#2412B5]" data-testid="footer-help">Help & Support</Link>
        </div>
      </div>
      <div className="border-t border-[#E2E8ED] py-5 text-center text-xs muted"><p className="mb-3 text-[13px] font-medium text-[#086477]" data-testid="odisha-branding">Proudly designed in Odisha, for Odias <span className="text-[#FF9700]">♥</span></p><Link to="/admin-login" className="inline-block mb-2 text-[#2412B5]" data-testid="footer-admin-login">Administrator login</Link><span className="mx-2 text-[#C4CDD5]">·</span><Link to="/q-log" className="inline-block mb-2 text-[#98A2B3] hover:text-[#2412B5]" data-testid="footer-qlog-link">Q Log</Link><br />© {new Date().getFullYear()} Nirogyam Diagnostics and Healthcare Pvt Ltd. All rights reserved.</div>
    </footer>
  );
}

export const PublicShell = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white">
    <PublicHeader />
    <main className="flex-1 page-enter">{children}</main>
    <PublicFooter />
  </div>
);
